import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import { VyneTable } from '@vynedental/design-system'

import { RootState } from '../../../appReducer'
import CopyText from '../../shared/CopyText'
import Paginator from '../../shared/Paginator'
import { SimplifeyeLogoIconSmall, VyneLogoIconSmall } from '../../shared/svgIcons'

import StaffMemberActions from './StaffMemberActions'

import './StaffTabTableBody.sass'

export type StaffTabTableBodyProps = {
    practiceId: string
    practice: Models.Practice
    searchSort: ModelsV2.Practice.StaffSort | undefined
    searchOrder: 'asc' | 'desc' | undefined
    searchPage: number
    setSearchSort: (sort: ModelsV2.Practice.StaffSort) => void
    setSearchOrder: (order: 'asc' | 'desc') => void
    setSearchPage: (page: number) => void
    refreshStaffList: () => void
}

type Props = StaffTabTableBodyProps

const StaffTabTableBody = ({
    practiceId,
    practice,
    searchOrder,
    searchSort,
    searchPage,
    setSearchPage,
    setSearchOrder,
    setSearchSort,
    refreshStaffList,
}: Props) => {
    const list = useSelector((state: RootState) => state.v2.practices.practiceStaff?.[practiceId]?.list || [])
    const pagination = useSelector(
        (state: RootState) => state.v2.practices.practiceStaff?.[practiceId]?.pagination || null,
    )
    const pstUserManagement = !useSelector((state: RootState) => state.app.featureFlags?.pstUserManagement)

    const loading = useSelector((state: RootState) => state.v2.practices.practiceStaff?.[practiceId]?.loading || false)

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.columnKey && sorter.order) {
            const newOrder = sorter.order === 'ascend' ? 'asc' : 'desc'
            setSearchSort(sorter.columnKey)
            setSearchOrder(newOrder)
        } else if (sorter && !sorter.order) {
            const flipped = searchOrder === 'desc' ? 'asc' : 'desc'
            setSearchOrder(flipped)
        }
    }

    const getSortOrder = (field: string) => {
        if (searchSort !== field) return null
        return searchOrder === 'asc' ? ('ascend' as const) : ('descend' as const)
    }

    const handlePageChange = (nextPage: number) => {
        setSearchPage(nextPage)
    }

    const columns = [
        {
            title: '',
            key: 'logo',
            width: '3%',
            render: (_: any, employee: any) =>
                employee.is_vyne_created ? (
                    <VyneLogoIconSmall />
                ) : (
                    <SimplifeyeLogoIconSmall className="simplifeye-icon" />
                ),
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '5%',
            render: (id: string) => <CopyText tooltip={true} text={id} />,
        },
        {
            title: 'Last Name',
            dataIndex: 'last_name',
            key: 'last_name',
            width: '15%',
            sorter: true,
            sortOrder: getSortOrder('last_name'),
            render: (value: string, employee: any) => (
                <div className={`${getCellClass(employee)} employee-first-name-wrapper`}>
                    <div className="employee-name">{value}</div>
                </div>
            ),
        },
        {
            title: 'First Name',
            dataIndex: 'first_name',
            key: 'first_name',
            width: '15%',
            sorter: true,
            sortOrder: getSortOrder('first_name'),
            render: (value: string, employee: any) => (
                <div className={`${getCellClass(employee)} employee-last-name-wrapper`}>
                    <div className="employee-name">{value}</div>
                </div>
            ),
        },
        {
            title: 'Email Address',
            dataIndex: 'username',
            key: 'username',
            width: '20%',
            sorter: true,
            sortOrder: getSortOrder('username'),
            render: (value: string, employee: any) => (
                <div className={`${getCellClass(employee)} employee-username-wrapper`}>
                    <div className="employee-username">{value}</div>
                </div>
            ),
        },
        {
            title: 'Role',
            key: 'simplifeye_role.value',
            width: '12%',
            sorter: true,
            sortOrder: getSortOrder('simplifeye_role.value'),
            render: (_: any, employee: any) => (
                <div className={getCellClass(employee)}>
                    {employee.simplifeye_role && employee.simplifeye_role.display}
                </div>
            ),
        },
        {
            title: 'Status',
            key: 'account_status.value',
            width: '10%',
            sorter: true,
            sortOrder: getSortOrder('account_status.value'),
            render: (_: any, employee: any) => (
                <div className={getCellClass(employee)}>
                    {employee.account_status?.display.replace('Permanently ', '')}
                </div>
            ),
        },
        ...(pstUserManagement
            ? [
                  {
                      title: 'Edit',
                      key: 'edit',
                      width: '5%',
                      render: (_: any, employee: any) => (
                          <StaffMemberActions
                              type="edit"
                              practice={practice}
                              employee={employee}
                              refreshStaffList={refreshStaffList}
                          />
                      ),
                  },
              ]
            : []),
        {
            title: '',
            key: 'actions',
            width: '5%',
            render: (_: any, employee: any) => (
                <StaffMemberActions
                    type="menu"
                    practice={practice}
                    employee={employee}
                    refreshStaffList={refreshStaffList}
                />
            ),
        },
    ]

    if (list == null) {
        return (
            <div className="circular-progress-loader-background">
                <div className="circular-progress-loader">
                    <CircularProgress
                        className="circular-progress-spinner"
                        size={50}
                        color="primary"
                        variant="indeterminate"
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="staff-table-body">
            <VyneTable
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={false}
                tableLayout="fixed"
                onChange={handleTableChange}
                dataTestId="staff-table"
            />
            {pagination?.allRows && pagination?.allRows > 8 && (
                <div className="staff-paginator-wrapper" style={{ paddingBottom: pstUserManagement ? '28px' : '0px' }}>
                    <Paginator currentPage={searchPage} paginationInfo={pagination} selectPage={handlePageChange} />
                </div>
            )}
        </div>
    )
}

const getCellClass = (employee: any) => {
    const isInvited = employee.account_status?.value === 'invited'
    const isDeletedOrDeactivated =
        employee.account_status?.value === 'permanently_deleted' ||
        employee.account_status?.value === 'deleted' ||
        employee.account_status?.value === 'deactivated'

    return [isInvited ? 'invited' : '', isDeletedOrDeactivated ? 'deleteddeactivated' : ''].filter(Boolean).join(' ')
}

export default StaffTabTableBody
